import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { useLocation } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Avatar, Back, Breadcrumbs } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { userModel } from '@/entities/user';

import { useIsPresignMode } from '@/features/presignAuth';

import { MOBILE_ICON_SIZE } from '../config/constants';
import { useMobileBreadcrumbs } from '../hooks';

import { HeaderBanners } from './HeaderBanners/HeaderBanners';
import { HeaderDivider } from './HeaderDivider/HeaderDivider';
import { HeaderDrawer } from './HeaderDrawer/HeaderDrawer';
import { HeaderSettingsWrapper } from './HeaderSettingsWrapper/HeaderSettingsWrapper';
import { Logo } from './Logo/Logo';
import styles from './MobileHeader.module.scss';

type HeaderProps = {
  className?: string;
  currentPageLabel?: string;
  hideBreadcrumbs?: boolean;
  isProtectedPages?: boolean;
  showBackButton?: boolean;
  leadingActions?: ReactNode;
  onBackButtonClick?: () => void;
};

export const MobileHeader: FC<HeaderProps> = (props) => {
  const {
    className,
    currentPageLabel,
    hideBreadcrumbs = false,
    isProtectedPages = true,
    showBackButton = false,
    leadingActions,
    onBackButtonClick,
  } = props;

  const location = useLocation();
  const dispatch = useAppDispatch();
  const { userFullName, userInitials, userAvatarAssetSrc } = useAppSelector(
    userModel.selectors.selectUserAvatarData,
  );

  const isPresignMode = useIsPresignMode();
  const isPrintPage = location.pathname.includes('preview');

  const breadCrumbs = useMobileBreadcrumbs(currentPageLabel);

  const openDrawer = () => {
    dispatch(modalModel.actions.openModal({ modalID: ModalID.HeaderDrawer }));
  };

  const shouldShowBreadcrumbs =
    !showBackButton && !hideBreadcrumbs && Object.keys(breadCrumbs).length > 0;

  return (
    <>
      <header
        className={cn(styles.container, className)}
        data-section="mobile-header"
      >
        <div className={styles.topRow}>
          <Logo isMobile isClickable={!isPresignMode} />

          <div className={styles.rightCluster}>
            {leadingActions}

            <HeaderSettingsWrapper
              className={styles.settingsWrapper}
              isMobile
              isProtectedPages={isProtectedPages}
            >
              {isProtectedPages && (
                <button
                  type="button"
                  className={styles.avatarButton}
                  onClick={openDrawer}
                >
                  <Avatar
                    size={MOBILE_ICON_SIZE}
                    src={userAvatarAssetSrc}
                    alt={userFullName}
                    initials={userInitials}
                  />
                </button>
              )}
            </HeaderSettingsWrapper>
          </div>
        </div>

        <HeaderDivider />
      </header>

      <HeaderBanners
        className={cn(
          (showBackButton || shouldShowBreadcrumbs) && styles.bannersWithMargin,
        )}
      />

      {shouldShowBreadcrumbs && (
        <Breadcrumbs
          breadCrumbs={breadCrumbs}
          className={styles.breadcrumbs}
          isClickable={!isPresignMode}
          isPrintPage={isPrintPage}
        />
      )}

      {showBackButton && <Back onClick={onBackButtonClick} />}

      <HeaderDrawer />
    </>
  );
};
